import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { GenerationProgressController, type GenerationProgressSnapshot } from './daily-forecast-progress';

export type OrbitNumber = { id: string; value: number; label: string };

type Orbit = { rx: number; ry: number; tilt: number; seconds: number; reverse?: boolean };

const CENTER = 100;
const ORBIT_A: Orbit = { rx: 90, ry: 33, tilt: -16, seconds: 9 };
const ORBIT_B: Orbit = { rx: 72, ry: 50, tilt: 27, seconds: 13, reverse: true };

/** Rider lit per stage, by index in `numbers` (year, month, day): it follows what the stage copy names. */
const LIT_BY_STAGE = [2, 0, 2, 1, 0, 1, 2] as const;

const round = (value: number) => Math.round(value * 100) / 100;

function orbitPath({ rx, ry, tilt }: Orbit): string {
  const angle = (tilt * Math.PI) / 180;
  const dx = rx * Math.cos(angle);
  const dy = rx * Math.sin(angle);
  const start = `${round(CENTER - dx)} ${round(CENTER - dy)}`;
  const end = `${round(CENTER + dx)} ${round(CENTER + dy)}`;
  return `M ${start} A ${rx} ${ry} ${tilt} 1 1 ${end} A ${rx} ${ry} ${tilt} 1 1 ${start}`;
}

/** Static resting point on the orbit for reduced motion; `phase` is the share of one revolution. */
function orbitPoint({ rx, ry, tilt }: Orbit, phase: number): { x: number; y: number } {
  const angle = (tilt * Math.PI) / 180;
  const t = Math.PI + phase * 2 * Math.PI;
  return {
    x: round(CENTER + rx * Math.cos(t) * Math.cos(angle) - ry * Math.sin(t) * Math.sin(angle)),
    y: round(CENTER + rx * Math.cos(t) * Math.sin(angle) + ry * Math.sin(t) * Math.cos(angle)),
  };
}

function useGenerationProgress(startedAt: number, succeeded: boolean, onRevealReady: () => void): GenerationProgressSnapshot {
  const controller = useMemo(() => new GenerationProgressController(startedAt), [startedAt]);
  const [snapshot, setSnapshot] = useState(() => controller.tick(Date.now()));
  const revealRef = useRef(onRevealReady);
  useEffect(() => { revealRef.current = onRevealReady; }, [onRevealReady]);

  useEffect(() => {
    if (!succeeded) return;
    controller.complete(Date.now());
    setSnapshot(controller.tick(Date.now()));
  }, [controller, succeeded]);

  useEffect(() => {
    let timer = 0;
    let revealed = false;
    const step = () => {
      const next = controller.tick(Date.now());
      setSnapshot(next);
      if (next.readyToReveal) {
        if (!revealed) { revealed = true; revealRef.current(); }
        return;
      }
      timer = window.setTimeout(step, controller.isCompleting ? 32 : 100);
    };
    step();
    return () => window.clearTimeout(timer);
  }, [controller]);

  return snapshot;
}

function OrbitRider({ orbit, path, phase, animate, children }: {
  orbit: Orbit; path: string; phase: number; animate: boolean; children: ReactNode;
}) {
  if (!animate) {
    const point = orbitPoint(orbit, phase);
    return <g transform={`translate(${point.x} ${point.y})`}>{children}</g>;
  }
  return <g>
    {children}
    <animateMotion
      dur={`${orbit.seconds}s`}
      begin={`${round(-phase * orbit.seconds)}s`}
      repeatCount="indefinite"
      path={path}
      {...(orbit.reverse ? { keyPoints: '1;0', keyTimes: '0;1', calcMode: 'linear' } : {})}
    />
  </g>;
}

function OrbitNode({ lit }: { lit: boolean }) {
  return <g className={'dfg-node' + (lit ? ' is-lit' : '')}>
    <circle className="dfg-node-glow" r="7" />
    <circle className="dfg-node-core" r="2.6" />
  </g>;
}

function OrbitNumeral({ value, lit }: { value: number; lit: boolean }) {
  return <g className={'dfg-numeral' + (lit ? ' is-lit' : '')}>
    <circle className="dfg-numeral-disc" r="10.5" />
    <text className="dfg-numeral-text" textAnchor="middle" dominantBaseline="central">{value}</text>
  </g>;
}

/**
 * Premium generation ritual for a fresh daily forecast. `succeeded` must come from the real API
 * response; only then does the progress run to 100% and call `onRevealReady` after a short hold.
 */
export function DailyForecastGenerationProgress({
  startedAt,
  succeeded,
  numbers,
  onRevealReady,
}: {
  startedAt: number;
  succeeded: boolean;
  numbers?: OrbitNumber[];
  onRevealReady: () => void;
}) {
  const reduceMotion = useReducedMotion() ?? false;
  const animate = !reduceMotion;
  const snapshot = useGenerationProgress(startedAt, succeeded, onRevealReady);
  const gradientId = useId().replace(/:/g, '');
  const pathA = orbitPath(ORBIT_A);
  const pathB = orbitPath(ORBIT_B);
  const realNumbers = numbers?.length === 3 ? numbers : null;
  const litIndex = snapshot.isCompleting ? -1 : LIT_BY_STAGE[snapshot.stageIndex] ?? 2;
  const allLit = snapshot.isCompleting;
  const lit = (index: number) => allLit || litIndex === index;
  const textMotion = {
    initial: { opacity: 0, y: animate ? 6 : 0 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.2, ease: 'easeOut' as const } },
    exit: { opacity: 0, y: animate ? -5 : 0, transition: { duration: 0.12, ease: 'easeIn' as const } },
  };
  const rider = (index: number) => realNumbers
    ? <OrbitNumeral value={realNumbers[index]!.value} lit={lit(index)} />
    : <OrbitNode lit={lit(index)} />;

  return <div
    className={'dfg forecast-flow' + (snapshot.isCompleting ? ' is-complete' : '') + (snapshot.isLeaving ? ' is-leaving' : '')}
    data-stage={snapshot.stageIndex}
    data-motion={animate ? 'full' : 'reduced'}
  >
    <p className="today-overline forecast-kicker">Прогноз дня</p>
    <div className="dfg-visual" aria-hidden="true">
      <span className="dfg-aura" />
      <span className="dfg-halo" />
      <span className="dfg-core"><span className="dfg-core-shine" /></span>
      <span className="dfg-flare" key={snapshot.stageIndex} />
      <span className="dfg-mote mote-a" /><span className="dfg-mote mote-b" /><span className="dfg-mote mote-c" />
      <span className="dfg-mote mote-d" /><span className="dfg-mote mote-e" />
      <svg className="dfg-orbits" viewBox="0 0 200 200" focusable="false">
        <defs>
          <linearGradient id={`${gradientId}-a`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#efd09d" stopOpacity=".05" />
            <stop offset="45%" stopColor="#efd09d" stopOpacity=".7" />
            <stop offset="100%" stopColor="#b78fe4" stopOpacity=".18" />
          </linearGradient>
          <linearGradient id={`${gradientId}-b`} x1="1" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#b78fe4" stopOpacity=".08" />
            <stop offset="55%" stopColor="#c9a5ef" stopOpacity=".55" />
            <stop offset="100%" stopColor="#efd09d" stopOpacity=".12" />
          </linearGradient>
        </defs>
        <path className="dfg-orbit orbit-b" d={pathB} stroke={`url(#${gradientId}-b)`} />
        <path className="dfg-orbit orbit-a" d={pathA} stroke={`url(#${gradientId}-a)`} />
        <OrbitRider orbit={ORBIT_B} path={pathB} phase={0.18} animate={animate}>{rider(0)}</OrbitRider>
        <OrbitRider orbit={ORBIT_A} path={pathA} phase={0.08} animate={animate}>{rider(1)}</OrbitRider>
        <OrbitRider orbit={ORBIT_A} path={pathA} phase={0.58} animate={animate}>{rider(2)}</OrbitRider>
        <OrbitRider orbit={ORBIT_B} path={pathB} phase={0.68} animate={animate}><OrbitNode lit={allLit} /></OrbitRider>
        <OrbitRider orbit={ORBIT_A} path={pathA} phase={0.33} animate={animate}><circle className="dfg-spark" r="1.4" /></OrbitRider>
      </svg>
    </div>
    <div className="dfg-percent" aria-hidden="true">
      <span className="dfg-percent-value">{snapshot.displayPercent}</span><span className="dfg-percent-sign">%</span>
    </div>
    <div
      className="dfg-bar"
      role="progressbar"
      aria-label="Подготовка прогноза дня"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={snapshot.displayPercent}
    >
      <span className="dfg-bar-fill" style={{ width: `${snapshot.percentage}%` }}><span className="dfg-bar-sheen" /></span>
    </div>
    <div className="dfg-copy" aria-live="polite" aria-atomic="true">
      <AnimatePresence mode="wait" initial={false}>
        <motion.p key={snapshot.title} className="dfg-title" {...textMotion}>{snapshot.title}</motion.p>
      </AnimatePresence>
      <AnimatePresence mode="wait" initial={false}>
        <motion.p key={snapshot.support} className="dfg-support" {...textMotion}>{snapshot.support}</motion.p>
      </AnimatePresence>
    </div>
    {realNumbers && <p className="dfg-legend" aria-hidden="true">
      {realNumbers.map(item => <span key={item.id}><b>{item.value}</b>{item.label}</span>)}
    </p>}
  </div>;
}
